import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded"
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded"
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded"
import DeselectRoundedIcon from "@mui/icons-material/DeselectRounded"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import FitScreenIcon from "@mui/icons-material/FitScreen"
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded"
import PauseIcon from "@mui/icons-material/Pause"
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded"
import SelectAllRoundedIcon from "@mui/icons-material/SelectAllRounded"
import SkipNextRoundedIcon from "@mui/icons-material/SkipNext"
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded"
import { Button, ButtonGroup } from "@mui/material"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Tooltip from "@mui/material/Tooltip"
import { t } from "i18next"
import { useEffect, useState } from "react"
import { getCurrentTime } from "../../../../hooks/useCurrentTime"
import { AUTO_ANIMATE_PRESETS, useAutoAnimations } from "../../../../hooks/useAutoAnimations"
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux"
import { SliderTimeProps } from "../../../../hooks/useSliderTime"
import useTimeStepsDensity from "../../../../hooks/useTimeStepsDensity"
import { setTimelinePreferencesAsync } from "../../../../store/slices/timelinePreferences.slice"
import { TIMELINE_ROW } from "../../../../style/sizing"
import {
    timelineMaxZoom,
    timelineMinZoom,
    timelineZoomStep,
} from "../../../../data/defaults/timeline.zoom.defaults"
import ZoomSlider from "../../../common/ZoomSlider"
import TimelineCodeTriggers from "./TimelineTools/TimelineCodeTriggers"
import TimelineControlsRows from "./TimelineTools/TimelineControlRows"
import TimelineTimeField from "./TimelineTools/TimelineTimeField"

interface Props {
    visibility: "visible" | "hidden"
    disabled: boolean
    handleFitTimeline: () => void
    sliderTimeProps: SliderTimeProps
}

const TimelineControlTools = ({
    visibility,
    disabled,
    handleFitTimeline,
    sliderTimeProps,
}: Props) => {
    const dispatch = useAppDispatch()
    const {
        generateIntroOutro,
        clearAllAnimations,
        selectAllAnimationsAction,
        deselectAllAnimations,
        hasAnyAnimations,
        objectCount,
    } = useAutoAnimations()
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null)
    const masterTimeline = useAppSelector((state) => state.masterTimeline.value)
    const timelinePreferences = useAppSelector((state) => state.timelinePreferences.value)
    const frame = timelinePreferences.framerate
    const duration = masterTimeline?.duration() || 0
    const isPlaying = useAppSelector((state) => state.masterTimeline.isPlaying)

    const { play, pause, next, outro, isValid, handleTimeChange, goToBegin, formattedDuration } =
        sliderTimeProps

    const { setDensityOfTimeline } = useTimeStepsDensity()

    const timelineZoom = timelinePreferences.timelineZoom

    const setTimelineTweeensPerSec = (timelineZoom: number) => {
        setDensityOfTimeline(timelineZoom)
    }

    const setTimelineZoom = (zoomValue: number) => {
        setTimelineTweeensPerSec(Math.min(zoomValue, timelineMaxZoom))
        dispatch(setTimelinePreferencesAsync("timelineZoom", Math.max(zoomValue, timelineMinZoom)))
    }
    useEffect(() => {
        setDensityOfTimeline(timelineZoom)
    }, [setDensityOfTimeline, timelineZoom])

    const handleNext = () => {
        next()
    }

    const handleOutro = () => {
        outro()
    }

    return (
        <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            p={1}
            my={0} // prevent unexpected vertical shift caused by MUI Grid's internal spacing system, spacing add negative margin from bottom and top
            visibility={visibility ? "visible" : "hidden"}
            spacing={1}
        >
            <Grid
                id="editor-step-5"
                item
                sx={{
                    width: TIMELINE_ROW.headingWidth - 6,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <TimelineTimeField
                    isValid={isValid}
                    frame={frame}
                    formattedDuration={formattedDuration}
                    pause={pause}
                    handleTimeChange={handleTimeChange}
                />
            </Grid>
            <Grid item>
                <Tooltip title={t("editor:timeline.goToBeginButton")}>
                    <span>
                        <IconButton
                            aria-label="go-to-begin"
                            sx={{ color: "#fff" }}
                            size="large"
                            onClick={goToBegin}
                            disabled={!isPlaying && duration === 0}
                        >
                            <SkipPreviousRoundedIcon />
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip
                    title={
                        isPlaying
                            ? t("editor:timeline.pauseButton")
                            : t("editor:timeline.playButton")
                    }
                >
                    <span>
                        <IconButton
                            aria-label={isPlaying ? "pause" : "play"}
                            sx={{ color: "#fff" }}
                            size="large"
                            onClick={() => (isPlaying ? pause(getCurrentTime()) : play())}
                            disabled={!isPlaying && duration === 0}
                        >
                            {isPlaying ? <PauseIcon /> : <PlayArrowRoundedIcon />}
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title={t("editor:timeline.nextButton")}>
                    <span>
                        <IconButton
                            aria-label="next"
                            sx={{ color: "#fff" }}
                            size="large"
                            onClick={handleNext}
                            disabled={!masterTimeline || duration === 0}
                        >
                            <SkipNextRoundedIcon />
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title={t("editor:timeline.outroButton")}>
                    <span>
                        <IconButton
                            aria-label="outro"
                            sx={{ color: "#fff" }}
                            size="large"
                            onClick={handleOutro}
                            disabled={!masterTimeline || duration === 0}
                        >
                            <ExitToAppIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Grid>
            <Grid
                item
                sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}
            >
                <ButtonGroup
                    size="small"
                    variant="outlined"
                    disabled={objectCount === 0 || hasAnyAnimations()}
                >
                    <Tooltip title={t("editor:timeline.autoAnimateTooltip")}>
                        <span>
                            <Button
                                startIcon={<AutoFixHighRoundedIcon />}
                                onClick={() => generateIntroOutro("fade")}
                                disabled={objectCount === 0 || hasAnyAnimations()}
                                sx={{ textTransform: "none", fontSize: 12 }}
                            >
                                {t("editor:timeline.autoAnimate")}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        size="small"
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        disabled={objectCount === 0 || hasAnyAnimations()}
                        sx={{ px: 0.5, minWidth: "auto" }}
                    >
                        <ArrowDropDownRoundedIcon fontSize="small" />
                    </Button>
                </ButtonGroup>
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                >
                    {AUTO_ANIMATE_PRESETS.map((preset) => (
                        <MenuItem
                            key={preset.id}
                            onClick={() => {
                                generateIntroOutro(preset.id)
                                setMenuAnchor(null)
                            }}
                            disabled={objectCount === 0 || hasAnyAnimations()}
                        >
                            <ListItemIcon>
                                <preset.Icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t(preset.labelKey)}</ListItemText>
                        </MenuItem>
                    ))}
                </Menu>
                <TimelineCodeTriggers disabled={disabled} />
            </Grid>
            <Grid item xs flexGrow={1}>
                <ZoomSlider
                    scalePerc={Math.round((timelineZoom / 100) * 100)}
                    zoom={timelineZoom}
                    zoomStep={timelineZoomStep}
                    minZoom={timelineMinZoom}
                    maxZoom={timelineMaxZoom}
                    disabled={disabled}
                    handleSliderChange={setTimelineZoom}
                />
            </Grid>
            <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={t("editor:fitTimelineButton")}>
                    <span>
                        <IconButton disabled={disabled} onClick={handleFitTimeline}>
                            <FitScreenIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>

                <TimelineControlsRows disabled={disabled || !masterTimeline} />

                <IconButton size="small" onClick={(e) => setMoreMenuAnchor(e.currentTarget)}>
                    <MoreVertRoundedIcon fontSize="small" />
                </IconButton>
                <Menu
                    anchorEl={moreMenuAnchor}
                    open={Boolean(moreMenuAnchor)}
                    onClose={() => setMoreMenuAnchor(null)}
                >
                    <MenuItem
                        onClick={() => {
                            selectAllAnimationsAction()
                            setMoreMenuAnchor(null)
                        }}
                        disabled={!hasAnyAnimations()}
                    >
                        <ListItemIcon>
                            <SelectAllRoundedIcon />
                        </ListItemIcon>
                        <ListItemText>{t("editor:timeline.selectAllAnimations")}</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            deselectAllAnimations()
                            setMoreMenuAnchor(null)
                        }}
                    >
                        <ListItemIcon>
                            <DeselectRoundedIcon />
                        </ListItemIcon>
                        <ListItemText>{t("editor:timeline.deselectAll")}</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            clearAllAnimations()
                            setMoreMenuAnchor(null)
                        }}
                        disabled={!hasAnyAnimations()}
                        sx={{ borderTop: "1px solid", borderColor: "divider", mt: 0.5 }}
                    >
                        <ListItemIcon sx={{ color: "error.main" }}>
                            <DeleteSweepRoundedIcon />
                        </ListItemIcon>
                        <ListItemText sx={{ color: "error.main" }}>
                            {t("editor:timeline.clearTimeline")}
                        </ListItemText>
                    </MenuItem>
                </Menu>
            </Grid>
        </Grid>
    )
}

export default TimelineControlTools
